import React from "react";

import ParentAndFirstChildLine from "./components/ParentAndFirstChildLine";
import ParentWithMultipleChildsLine from "./components/ParentWithMultipleChildsLine";
import SingleVerticalLine from "./components/SingleVerticalLine";
import {
  arrayWithOnlyOneSameValue,
  checkIfNextLevelHasLowerNumber,
  indexOfNextSameValueInALevelsArray
} from "./functions";

interface ICompanyHierarchyTreeLinesProps {
  indexOfxAxisEmptyDiv: number;
  companyLevelsList: number[];
  tableRowIndex?: number;
  xAxisEmptyDivsCount: number;
}

const CompanyHierarchyTreeLines = ({
  indexOfxAxisEmptyDiv,
  companyLevelsList,
  tableRowIndex,
  xAxisEmptyDivsCount
}: ICompanyHierarchyTreeLinesProps) => {
  const isRowInRange =
    tableRowIndex !== undefined &&
    tableRowIndex >= 0 &&
    tableRowIndex < companyLevelsList.length;

  const isColumnInRange =
    indexOfxAxisEmptyDiv >= 0 && indexOfxAxisEmptyDiv < xAxisEmptyDivsCount;

  if (!isRowInRange || !isColumnInRange) {
    return null;
  }

  // The old version looped over the whole list, slicing it on every iteration,
  // and only used the iteration where index === tableRowIndex. Jump straight to it.
  const currentCompanyLevel = companyLevelsList[tableRowIndex];
  const gapBetweenLevels =
    currentCompanyLevel - companyLevelsList[tableRowIndex + 1];
  const levelListWithoutCurrentLevel = companyLevelsList.slice(
    tableRowIndex + 1
  );
  const levelNumber = indexOfxAxisEmptyDiv + 1;

  const isOwnLevelColumn = currentCompanyLevel === levelNumber;
  const hasLevelBelow = levelListWithoutCurrentLevel.includes(levelNumber);

  // Cheap checks first: the expensive helpers below are only needed when
  // a vertical line is actually possible.
  if (!hasLevelBelow) {
    return isOwnLevelColumn ? <ParentAndFirstChildLine /> : null;
  }

  const canBeParentWithMultipleChilds =
    gapBetweenLevels === -1 || gapBetweenLevels === 0;

  if (isOwnLevelColumn && !canBeParentWithMultipleChilds) {
    return <ParentAndFirstChildLine />;
  }

  const nextSameValueIndex = indexOfNextSameValueInALevelsArray(
    levelListWithoutCurrentLevel,
    levelNumber
  );

  const arrayWithOneSameValue = arrayWithOnlyOneSameValue(
    levelListWithoutCurrentLevel,
    nextSameValueIndex
  );

  const isNextLevelLowerNumber = checkIfNextLevelHasLowerNumber(
    levelListWithoutCurrentLevel,
    nextSameValueIndex,
    levelNumber
  );

  const shouldHaveVerticalLine =
    arrayWithOneSameValue.includes(levelNumber) && !isNextLevelLowerNumber;

  if (isOwnLevelColumn) {
    return shouldHaveVerticalLine ? (
      <ParentWithMultipleChildsLine />
    ) : (
      <ParentAndFirstChildLine />
    );
  }

  return shouldHaveVerticalLine ? <SingleVerticalLine /> : null;
};

export default React.memo(CompanyHierarchyTreeLines);
